import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";

interface IProps {
  used: number;
  pending: number;
  maximum: number;
}

/**
 * A stacked progress bar that displays a used and pending value against a maximum.
 *
 * If `used + pending > maximum` then we show the pending bar in red:
 *
 *  <-------------><--------------------------->
 *        used          pending (Biarri Red)
 *
 * If `used + pending == maximum` then the pending bar is shown in orange instead of red.
 * If `used + pending < maximum` then there are three segments:
 *
 * <-------------><---------><----------------->
 *      used        pending       remaining
 *
 * @param used The current value of the progress bar.
 * @param pending The "pending" value that will be added to the used value.
 * @param maximum The maximum value of the progress bar.
 */
const StackedProgress: React.FC<IProps> = ({ used, pending, maximum }: IProps) => {
  return (
    <Box position="relative" height={4}>
      {/* Pending bar */}
      <LinearProgress
        variant="determinate"
        data-testid="stacked-progress-pending"
        value={Math.min(1, (used + pending) / maximum) * 100}
        sx={(theme) => ({
          "& .MuiLinearProgress-bar": {
            backgroundColor: used + pending > maximum ? theme.palette.biarri.primary.red : "rgba(255, 140, 0, 0.6)",
          },
        })}
      />
      {/* Used bar */}
      <LinearProgress
        variant="determinate"
        data-testid="stacked-progress-used"
        value={Math.min(1, used / maximum) * 100}
        sx={{
          position: "absolute",
          top: 0,
          width: "100%",
          backgroundColor: "transparent",
        }}
      />
    </Box>
  );
};

export default StackedProgress;
